// ============================================================================
// COMPLIANCE MATRIX VIEW — story #176
// Flat, cross-document compliance list — the "spine of bid working sessions"
// (.claude/plans/bid-response-feature-plan.md). Unlike RequirementsPanel
// (BidProjectHome.tsx), which is scoped to one expanded tender document, this
// view aggregates every BidRequirement across the whole project in one list.
// Explicitly NOT in scope here: owner assignment / manual status edit / answer
// linking (#178), weighted rollup total (#181, deferred until real client
// evaluation-criteria formats are seen).
// Rendering lives in ComplianceMatrixTable.tsx (kept dependency-free of the
// data hooks so it stays cheaply unit-testable); this file just wires it up.
// ============================================================================

import React, { useMemo } from 'react';
import { Loader2, ListChecks } from 'lucide-react';
import { useBidDocuments, useBidRequirements } from '../../hooks/useBid';
import { ComplianceMatrixTable } from './ComplianceMatrixTable';

interface ComplianceMatrixViewProps {
  projectId: string;
}

export function ComplianceMatrixView({ projectId }: ComplianceMatrixViewProps) {
  const { data: requirements, isLoading: requirementsLoading } = useBidRequirements(projectId);
  const { data: documents, isLoading: documentsLoading } = useBidDocuments(projectId, 'tender');

  const documentNamesById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const doc of documents ?? []) map[doc.documentId] = doc.fileName;
    return map;
  }, [documents]);

  const isLoading = requirementsLoading || documentsLoading;

  return (
    <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
      <div className="px-5 py-4 border-b border-[#e2e8f0] flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center">
          <ListChecks size={16} />
        </div>
        <div>
          <h2 className="text-sm font-bold text-navy">Compliance Matrix</h2>
          <p className="text-xs text-slate-500">Every requirement across all tender documents, in one place</p>
        </div>
      </div>
      {isLoading ? (
        <div className="flex items-center justify-center py-12 text-slate-500 text-sm">
          <Loader2 size={20} className="animate-spin mr-2" /> Loading requirements...
        </div>
      ) : (
        <ComplianceMatrixTable requirements={requirements ?? []} documentNamesById={documentNamesById} />
      )}
    </div>
  );
}

export default ComplianceMatrixView;
